import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { LogOut, User, Menu, KeyRound, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { changePassword } from "../lib/api";

const PIMS_LOGO = "https://customer-assets.emergentagent.com/job_gpass-approval-hub/artifacts/aqa37c60_pims%20logo.png";

const roleLabels = {
  student: "Student",
  coordinator: "Class Coordinator",
  principal: "Principal",
  vice_principal: "Vice Principal",
  hostel_warden: "Hostel Warden",
  gate_operator: "Gate Operator",
  reception: "Reception",
  admin: "Administrator",
};

export const DashboardLayout = ({ children, title, actions }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const [showChangePw, setShowChangePw] = useState(false);
  const [pwData, setPwData] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [pwLoading, setPwLoading] = useState(false);

  const handleChangePassword = async () => {
    if (!pwData.current_password || !pwData.new_password) {
      toast.error("Fill all fields"); return;
    }
    if (pwData.new_password !== pwData.confirm_password) {
      toast.error("New passwords don't match"); return;
    }
    if (pwData.new_password.length < 4) {
      toast.error("Password must be at least 4 characters"); return;
    }
    setPwLoading(true);
    try {
      await changePassword({ current_password: pwData.current_password, new_password: pwData.new_password });
      toast.success("Password changed successfully!");
      setShowChangePw(false);
      setPwData({ current_password: "", new_password: "", confirm_password: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setPwLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-200 sticky top-0 z-50" data-testid="dashboard-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo & Title */}
            <div className="flex items-center gap-4">
              <img 
                src={PIMS_LOGO} 
                alt="PIMS" 
                className="h-10"
                data-testid="header-logo"
              />
              <div className="hidden sm:block">
                <h1 className="text-lg font-semibold text-slate-900">{title}</h1>
                <p className="text-xs text-slate-500">{roleLabels[user?.role] || "User"}</p>
              </div>
            </div>

            {/* Actions & Profile */}
            <div className="flex items-center gap-3">
              {actions}
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="gap-2" data-testid="user-menu-btn">
                    <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center">
                      <User className="h-4 w-4 text-slate-600" />
                    </div>
                    <span className="hidden sm:inline text-sm font-medium">{user?.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium">{user?.name}</p>
                      <p className="text-xs text-slate-500">{user?.email}</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="text-slate-500 text-xs">
                    Role: {roleLabels[user?.role]}
                  </DropdownMenuItem>
                  {user?.enrollment_number && (
                    <DropdownMenuItem className="text-slate-500 text-xs">
                      ID: {user.enrollment_number}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={() => setShowChangePw(true)}
                    className="cursor-pointer"
                    data-testid="change-password-btn"
                  >
                    <KeyRound className="mr-2 h-4 w-4" />
                    Change Password
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={handleLogout}
                    className="text-red-600 cursor-pointer"
                    data-testid="logout-btn"
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      {/* Change Password Dialog */}
      <Dialog open={showChangePw} onOpenChange={setShowChangePw}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Change Password</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Current Password</Label>
              <Input
                type="password"
                value={pwData.current_password}
                onChange={(e) => setPwData({ ...pwData, current_password: e.target.value })}
                placeholder="Enter current password"
                data-testid="current-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label>New Password</Label>
              <Input
                type="password"
                value={pwData.new_password}
                onChange={(e) => setPwData({ ...pwData, new_password: e.target.value })}
                placeholder="Enter new password"
                data-testid="new-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Confirm New Password</Label>
              <Input
                type="password"
                value={pwData.confirm_password}
                onChange={(e) => setPwData({ ...pwData, confirm_password: e.target.value })}
                placeholder="Confirm new password"
                data-testid="confirm-password-input"
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={handleChangePassword} disabled={pwLoading} data-testid="save-password-btn">
                {pwLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <KeyRound className="h-4 w-4 mr-1" />}
                Save Password
              </Button>
              <Button variant="outline" onClick={() => setShowChangePw(false)}>Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
        {children}
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-white/60 mt-8 py-3" data-testid="app-footer">
        <p className="text-center text-xs text-slate-400">
          PIMS Gate Management System - Powered By Steward Healthcare India Pvt Ltd
        </p>
      </footer>
    </div>
  );
};

export default DashboardLayout;
